// src/fee-risk-profile-diagram.ts
function polarToCartesian(centerX, centerY, radiusX, radiusY, angleInDegrees) {
  const angleInRadians = angleInDegrees * Math.PI / 180;
  return {
    x: centerX + radiusX * Math.cos(angleInRadians),
    y: centerY - radiusY * Math.sin(angleInRadians)
  };
}
function describeArc(x, y, radiusX, radiusY, startAngle, endAngle) {
  const start = polarToCartesian(x, y, radiusX, radiusY, endAngle);
  const end = polarToCartesian(x, y, radiusX, radiusY, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
  const d = [
    "M",
    start.x,
    start.y,
    "A",
    radiusX,
    radiusY,
    0,
    largeArcFlag,
    1,
    end.x,
    end.y
  ].join(" ");
  return d;
}
var formatPrice = (value, currencySymbol, prefix, suffix) => {
  if (value === void 0) {
    return "";
  }
  const amount = Math.round(value).toLocaleString();
  return `${prefix ?? ""}${currencySymbol}${amount}${suffix ?? ""}`;
};
var STANDARD_FONT_SIZE = 16;
var STANDARD_WIDTH = 600;
var STANDARD_BORDER_RADIUS = 15;
var FONT_FAMILY = "Arial";
var colours = {
  blue: "#0082CB",
  red: "#F15928",
  orange: "#FFEACE",
  white: "#FFFFFF",
  black: "#000000",
  grey: "#777777"
};
var renderTextBubble = ({
  x,
  y,
  text,
  scaleFactor,
  colour,
  borderColour = colour,
  borderRadiusCoefficient = 1,
  textColour = colours.white,
  show = true,
  msWord = false
}) => {
  let outputString = "";
  const calculateTextDimensions = (text2) => {
    const textWidth2 = text2.length * 9 * scaleFactor;
    const textHeight2 = 15 * scaleFactor;
    return {
      textWidth: textWidth2,
      textHeight: textHeight2
    };
  };
  const { textWidth, textHeight } = calculateTextDimensions(text);
  const textSize = scaleFactor * STANDARD_FONT_SIZE;
  const rx = borderRadiusCoefficient * scaleFactor * STANDARD_BORDER_RADIUS;
  const ry = borderRadiusCoefficient * scaleFactor * STANDARD_BORDER_RADIUS;
  const padding = 1.5 * textHeight;
  const bubbleWidth = textWidth + padding;
  const bubbleHeight = textHeight + padding;
  const rectX = x - bubbleWidth / 2;
  const rectY = y - bubbleHeight / 2;
  const textY = msWord ? y + textSize / 3 : y;
  if (show) {
    outputString += `<g>`;
    outputString += `<rect x="${rectX}" y="${rectY}" rx="${rx}" ry="${ry}" width="${bubbleWidth}" height="${bubbleHeight}" fill="${colour}" stroke="${borderColour}" />`;
    outputString += `<text x="${x}" y="${textY}" fill="${textColour}" font-size="${textSize}" font-family="${FONT_FAMILY}" dominant-baseline="central" text-anchor="middle">${text}</text>`;
    outputString += `</g>`;
  }
  return outputString;
};
var createArrowMarker = (scaleFactor, colour, id) => {
  const markerWidth = 7.2 * scaleFactor;
  const markerHeight = 6 * scaleFactor;
  const refX = 7.2 * scaleFactor;
  const refY = 3 * scaleFactor;
  const points = `0 0, ${8 * scaleFactor} ${3 * scaleFactor}, 0 ${6 * scaleFactor}`;
  return `<marker id="${id}" markerUnits="userSpaceOnUse" markerWidth="${markerWidth}" markerHeight="${markerHeight}" refX="${refX}" refY="${refY}" orient="auto" fill="${colour}" stroke="${colour}">
    <polygon points="${points}" />
  </marker>`;
};
var renderSvgString = (svgProps, textBubbles, isFixedFee, isCappedFee, showArrows, msWord = false) => {
  const {
    totalWidth,
    totalHeight,
    thetaInflect,
    thetaAnticipated,
    centreX,
    centreY,
    circleRadius,
    secondCircleRadius,
    strokeWidth,
    scaleFactor,
    scaledStrokeDashArray,
    anticipatedUpperX,
    anticipatedUpperY,
    anticipatedLowerX,
    anticipatedLowerY,
    fixedFeeArrowStartX,
    fixedFeeArrowStartY,
    fixedFeeArrowEndX,
    fixedFeeArrowEndY,
    cappedFeeArrowStartX,
    cappedFeeArrowStartY,
    cappedFeeArrowEndX,
    cappedFeeArrowEndY,
    curveEndX,
    paddingLeft,
    paddingBottom,
    yStretch
  } = svgProps;
  let svgString = `<svg width="${totalWidth}" height="${totalHeight}" xmlns="http://www.w3.org/2000/svg">`;
  const blueArc = describeArc(
    centreX,
    centreY,
    circleRadius,
    yStretch * circleRadius,
    thetaAnticipated,
    180
  );
  svgString += `<path d="${blueArc}" fill="none" stroke="${colours.blue}" stroke-width="${strokeWidth}" />`;
  const redArc1 = describeArc(
    centreX,
    centreY,
    circleRadius,
    yStretch * circleRadius,
    thetaInflect - 2,
    thetaAnticipated
  );
  svgString += `<path d="${redArc1}" fill="none" stroke="${colours.red}" stroke-width="${strokeWidth}" />`;
  const redArc2 = describeArc(
    centreX + (circleRadius + secondCircleRadius) * Math.cos(thetaInflect * Math.PI / 180),
    centreY - yStretch * (circleRadius + secondCircleRadius) * Math.sin(thetaInflect * Math.PI / 180),
    secondCircleRadius,
    yStretch * secondCircleRadius,
    180 + thetaInflect,
    270
  );
  svgString += `<path d="${redArc2}" fill="none" stroke="${colours.red}" stroke-width="${strokeWidth}" />`;
  svgString += `<line x1="${centreX - circleRadius}" y1="${centreY}" x2="${centreX}" y2="${centreY}" stroke="${colours.blue}" stroke-width="${strokeWidth / 2}" stroke-dasharray="${scaledStrokeDashArray}" />`;
  svgString += `<line x1="${centreX}" y1="${centreY}" x2="${curveEndX}" y2="${centreY}" stroke="${colours.red}" stroke-width="${strokeWidth / 2}" stroke-dasharray="${scaledStrokeDashArray}" />`;
  svgString += `<line x1="${anticipatedLowerX}" y1="${anticipatedLowerY}" x2="${anticipatedUpperX}" y2="${anticipatedUpperY}" stroke="${colours.blue}" stroke-width="${strokeWidth / 2}" stroke-dasharray="${scaledStrokeDashArray}" />`;
  svgString += `<line x1="${centreX - circleRadius - 0.85 * paddingLeft - 0.5 * strokeWidth}" y1="${centreY + 0.8 * paddingBottom}" x2="${centreX + 1.5 * circleRadius}" y2="${centreY + 0.8 * paddingBottom}" stroke="${colours.grey}" stroke-width="${strokeWidth / 2}" marker-end="url(#greyArrowhead)" />`;
  svgString += `<line x1="${centreX - circleRadius - 0.85 * paddingLeft}" y1="${centreY + 0.8 * paddingBottom}" x2="${centreX - circleRadius - 0.85 * paddingLeft}" y2="${centreY - circleRadius}" stroke="${colours.grey}" stroke-width="${strokeWidth / 2}" marker-end="url(#greyArrowhead)" />`;
  svgString += `<text x="${centreX}" y="${centreY + paddingBottom}" fill="${colours.black}" font-size="${scaleFactor * STANDARD_FONT_SIZE}" font-family="${FONT_FAMILY}" text-anchor="middle">Fee Distribution (not to scale)</text>`;
  svgString += `<text x="${centreX - circleRadius - paddingLeft - 30}" y="${centreY - circleRadius / 2}" fill="${colours.black}" font-size="${scaleFactor * STANDARD_FONT_SIZE}" font-family="${FONT_FAMILY}" text-anchor="middle" transform="rotate(-90, ${centreX - circleRadius - paddingLeft}, ${centreY - circleRadius / 2})">Number of matters like this</text>`;
  svgString += `<defs>${createArrowMarker(
    2.4 * scaleFactor,
    colours.red,
    "redArrowhead"
  )}${createArrowMarker(
    2.4 * scaleFactor,
    colours.grey,
    "greyArrowhead"
  )}</defs>`;
  if (isFixedFee && showArrows) {
    svgString += `<line x1="${fixedFeeArrowStartX}" y1="${fixedFeeArrowStartY}" x2="${fixedFeeArrowEndX}" y2="${fixedFeeArrowEndY}" stroke="${colours.red}" stroke-width="${strokeWidth / 2}" marker-end="url(#redArrowhead)" />`;
  }
  if (isCappedFee && showArrows) {
    svgString += `<line x1="${cappedFeeArrowStartX}" y1="${cappedFeeArrowStartY}" x2="${cappedFeeArrowEndX}" y2="${cappedFeeArrowEndY}" stroke="${colours.red}" stroke-width="${strokeWidth / 2}" marker-end="url(#redArrowhead)" />`;
  }
  textBubbles.forEach((bubble) => {
    svgString += renderTextBubble({ ...bubble, msWord });
  });
  svgString += "</svg>";
  return svgString;
};
var renderFeeRiskProfileDiagram = async ({
  totalWidth = 800,
  currencySymbol,
  lowestAnticipatedFee,
  highestAnticipatedFee,
  lowestFee,
  highestFee,
  fixedFee,
  cappedFee,
  msWord = false
}) => {
  const yStretch = 1.2;
  const thetaAnticipated = 45;
  const thetaInflect = 22.5;
  const strokeWidthFactor = 0.01;
  const dashSize = 5;
  const paddingRight = 0.25 * totalWidth;
  const paddingLeft = 0.1 * totalWidth;
  const paddingBottom = 0.1 * totalWidth;
  const innerWidth = totalWidth - paddingRight - paddingLeft;
  const innerHeight = yStretch * innerWidth / 2;
  const totalHeight = innerHeight + paddingBottom;
  const circleRadius = 0.4 * innerWidth;
  const strokeWidth = innerWidth * strokeWidthFactor;
  const scaleFactor = innerWidth / STANDARD_WIDTH;
  const scaledStrokeDashArray = `${dashSize * scaleFactor},${dashSize * scaleFactor}`;
  const centreX = paddingLeft + innerWidth / 2;
  const centreY = 0.9 * innerHeight;
  const secondCircleRadius = Math.sin(thetaInflect * Math.PI / 180) / (1 - Math.sin(thetaInflect * Math.PI / 180)) * circleRadius;
  const secondCentreX = centreX + (circleRadius + secondCircleRadius) * Math.cos(thetaInflect * Math.PI / 180);
  const secondCentreY = centreY - yStretch * (circleRadius + secondCircleRadius) * Math.sin(thetaInflect * Math.PI / 180);
  const circleCoordinates = (th, r = 1) => ({
    x: centreX + r * circleRadius * Math.cos(th * Math.PI / 180),
    y: centreY - yStretch * r * circleRadius * Math.sin(th * Math.PI / 180)
  });
  const secondCircleCoordinates = (th, r = 1) => ({
    x: secondCentreX + r * secondCircleRadius * Math.cos(th * Math.PI / 180),
    y: secondCentreY - yStretch * r * secondCircleRadius * Math.sin(th * Math.PI / 180)
  });
  const showArrows = lowestFee !== void 0 && highestFee !== void 0;
  const hap = highestAnticipatedFee || 0;
  const lap = lowestAnticipatedFee || 0;
  const lp = lowestFee || 0;
  const hp = highestFee || 0;
  const ff = fixedFee || 0;
  const cf = cappedFee || 0;
  const redArcLength1 = (thetaAnticipated - thetaInflect) * Math.PI * circleRadius / 180;
  const redArcLength2 = (90 - thetaInflect) * Math.PI * secondCircleRadius / 180;
  const inflectionThresholdFee = hap + (hp - hap) * redArcLength1 / (redArcLength1 + redArcLength2);
  const feeToCoordinates = (fee) => {
    if (fee < lp) {
      return circleCoordinates(180, 1);
    }
    if (fee < lap) {
      const theta = 180 - (fee - lp) / (lap - lp) * thetaAnticipated;
      return circleCoordinates(theta, 1);
    } else if (fee < hap) {
      if (hap === 0) {
        return circleCoordinates(180, 1);
      }
      return circleCoordinates(
        180 - thetaAnticipated - (fee - lap) / (hap - lap) * (180 - 2 * thetaAnticipated),
        1
      );
    } else if (fee < inflectionThresholdFee) {
      const theta = thetaInflect + (inflectionThresholdFee - fee) / (inflectionThresholdFee - hap) * (thetaAnticipated - thetaInflect);
      return circleCoordinates(theta, 1);
    } else if (fee < hp) {
      const theta = (fee - inflectionThresholdFee) / (hp - inflectionThresholdFee) * (90 - thetaInflect);
      return secondCircleCoordinates(180 + thetaInflect + theta, 1);
    }
    return secondCircleCoordinates(270, 1);
  };
  const feeToLabelCoordinates = (fee, r) => {
    const { x, y } = feeToCoordinates(fee);
    return {
      x: centreX + (x - centreX) * (1 + (r - 1) * circleRadius / Math.sqrt((x - centreX) ** 2 + (y - centreY) ** 2)),
      y: centreY + (y - centreY) * (1 + (r - 1) * circleRadius / Math.sqrt((x - centreX) ** 2 + (y - centreY) ** 2))
    };
  };
  const { x: anticipatedUpperX, y: anticipatedUpperY } = circleCoordinates(thetaAnticipated);
  const { x: anticipatedLowerX, y: anticipatedLowerY } = circleCoordinates(
    180 - thetaAnticipated
  );
  const { x: inflectionX, y: inflectionY } = circleCoordinates(thetaInflect);
  const { x: fixedFeeArrowStartX, y: fixedFeeArrowStartY } = feeToLabelCoordinates(hap + (inflectionThresholdFee - hap) / 2, 1.7);
  const { x: fixedFeeArrowEndX, y: fixedFeeArrowEndY } = feeToLabelCoordinates(
    ff,
    1
  );
  const { x: cappedFeeArrowStartX, y: cappedFeeArrowStartY } = feeToLabelCoordinates(
    inflectionThresholdFee + (hp - inflectionThresholdFee) / 4,
    1.7
  );
  const { x: cappedFeeArrowEndX, y: cappedFeeArrowEndY } = feeToLabelCoordinates(cf, 1);
  const curveEndX = centreX + 1.5 * circleRadius;
  const curveEndY = centreY;
  const svgProps = {
    totalWidth,
    totalHeight,
    thetaInflect,
    thetaAnticipated,
    centreX,
    centreY,
    circleRadius,
    secondCircleRadius,
    strokeWidth,
    scaleFactor,
    scaledStrokeDashArray,
    anticipatedUpperX,
    anticipatedUpperY,
    anticipatedLowerX,
    anticipatedLowerY,
    fixedFeeArrowStartX,
    fixedFeeArrowStartY,
    fixedFeeArrowEndX,
    fixedFeeArrowEndY,
    cappedFeeArrowStartX,
    cappedFeeArrowStartY,
    cappedFeeArrowEndX,
    cappedFeeArrowEndY,
    curveEndX,
    paddingLeft,
    paddingBottom,
    yStretch
  };
  const bubbles = [
    {
      x: anticipatedUpperX,
      y: anticipatedUpperY,
      text: formatPrice(highestAnticipatedFee, currencySymbol),
      scaleFactor,
      colour: colours.blue
    },
    {
      x: anticipatedLowerX,
      y: anticipatedLowerY,
      text: formatPrice(lowestAnticipatedFee, currencySymbol),
      scaleFactor,
      colour: colours.blue
    },
    {
      x: centreX,
      y: anticipatedUpperY,
      text: "Anticipated Range",
      scaleFactor,
      colour: colours.blue
    },
    {
      x: centreX - circleRadius,
      y: centreY,
      text: formatPrice(lowestFee, currencySymbol),
      scaleFactor,
      colour: colours.blue
    },
    {
      x: curveEndX,
      y: curveEndY,
      text: formatPrice(highestFee, currencySymbol, "", "+"),
      scaleFactor,
      colour: colours.red
    },
    {
      x: inflectionX - 0.26 * circleRadius,
      y: inflectionY + 0.1 * circleRadius,
      text: "FEE RISK ZONE",
      scaleFactor,
      colour: colours.red
    },
    {
      x: centreX,
      y: centreY,
      text: "Lowest/Highest Range",
      scaleFactor,
      colour: colours.blue
    },
    {
      x: fixedFeeArrowStartX,
      y: fixedFeeArrowStartY,
      text: formatPrice(fixedFee, currencySymbol, "Fixed Fee: "),
      scaleFactor,
      colour: colours.orange,
      borderColour: colours.red,
      borderRadiusCoefficient: 0.1,
      textColour: colours.red,
      show: showArrows && fixedFee !== void 0 && fixedFee > 0
    },
    {
      x: cappedFeeArrowStartX,
      y: cappedFeeArrowStartY,
      text: formatPrice(cappedFee, currencySymbol, "Capped Fee: "),
      scaleFactor,
      colour: colours.orange,
      borderColour: colours.red,
      borderRadiusCoefficient: 0.1,
      textColour: colours.red,
      show: showArrows && cappedFee !== void 0 && cappedFee > 0
    }
  ];
  const svgString = renderSvgString(
    svgProps,
    bubbles,
    fixedFee !== void 0 && fixedFee > 0,
    cappedFee !== void 0 && cappedFee > 0,
    showArrows,
    msWord
  );
  return svgString;
};

// src/radial-chart.ts
var renderRadialChart = async ({
  data,
  labels = [],
  radius = 50,
  strokeWidth = 10,
  colors = ["#1890ff", "#52c41a", "#faad14", "#f5222d"]
}) => {
  const svgRadius = radius + strokeWidth;
  const svgSize = svgRadius * 2;
  const legendSpacing = 20;
  const legendItemHeight = 20;
  const legendWidth = 150;
  const totalSvgWidth = svgSize + legendWidth + legendSpacing;
  let svgContent = `<svg width="${totalSvgWidth}" height="${svgSize}" xmlns="http://www.w3.org/2000/svg">`;
  svgContent += `<g transform="translate(${svgRadius}, ${svgRadius})">`;
  data.forEach((fraction, index) => {
    const startAngle = -90;
    const endAngle = startAngle + fraction * 360;
    const startAngleRad = startAngle * Math.PI / 180;
    const endAngleRad = endAngle * Math.PI / 180;
    const innerRadius = radius - strokeWidth / 2;
    const outerRadius = radius + strokeWidth / 2;
    const largeArcFlag = fraction > 0.5 ? 1 : 0;
    const path = `M ${innerRadius * Math.cos(startAngleRad)} ${innerRadius * Math.sin(startAngleRad)} A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 1 ${innerRadius * Math.cos(endAngleRad)} ${innerRadius * Math.sin(endAngleRad)} L ${outerRadius * Math.cos(endAngleRad)} ${outerRadius * Math.sin(endAngleRad)} A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 0 ${outerRadius * Math.cos(startAngleRad)} ${outerRadius * Math.sin(startAngleRad)} Z`;
    svgContent += `<path d="${path.replace(/\n/g, " ")}" fill="${colors[index % colors.length]}" stroke="none" />
      <circle cx="${radius * Math.cos(endAngleRad)}" cy="${radius * Math.sin(endAngleRad)}" r="${strokeWidth / 2}" fill="${colors[index % colors.length]}" />`;
  });
  svgContent += `</g>`;
  svgContent += `<g transform="translate(${svgSize + legendSpacing}, ${legendItemHeight})">`;
  labels.forEach((label, index) => {
    const y = index * legendItemHeight * 1.5;
    svgContent += `<rect x="0" y="${y}" width="16" height="16" fill="${colors[index % colors.length]}" />`;
    svgContent += `<text x="20" y="${y + 12}" font-family="Arial" font-size="12">${label}</text>`;
  });
  svgContent += `</g>`;
  svgContent += `</svg>`;
  return svgContent;
};

// src/gantt-chart.ts
var COLORS = {
  phase: "#6a7bfd",
  task: "#42c776",
  grid: "#e0e0e0",
  axisLabel: "#555",
  text: "#333"
};
function wrapAndTruncate(text, maxChars) {
  if (!text) return [""];
  if (maxChars <= 3) return [text.slice(0, maxChars)];
  const words = text.split(" ");
  let line1 = "";
  let line2 = "";
  let currentLine = 1;
  for (const word of words) {
    if (word.length > maxChars) {
      const truncatedWord = word.slice(0, maxChars - 3).trimEnd() + "...";
      if (currentLine === 1) {
        if (!line1) {
          line1 = truncatedWord;
          currentLine = 2;
        } else {
          line2 = truncatedWord;
          break;
        }
      } else {
        line2 = truncatedWord;
        break;
      }
      continue;
    }
    if (currentLine === 1) {
      const testLine1 = (line1 ? line1 + " " : "") + word;
      if (testLine1.length <= maxChars) {
        line1 = testLine1;
      } else {
        currentLine = 2;
        if (word.length <= maxChars) {
          line2 = word;
        } else {
          line2 = word.slice(0, maxChars - 3).trimEnd() + "...";
          break;
        }
      }
    } else {
      const testLine2 = (line2 ? line2 + " " : "") + word;
      if (testLine2.length <= maxChars) {
        line2 = testLine2;
      } else {
        if (!line2.endsWith("...")) {
          line2 = line2.slice(0, maxChars - 3).trimEnd() + "...";
        }
        break;
      }
    }
  }
  line1 = line1.trim();
  line2 = line2.trim();
  if (line1 && line2) {
    if (line2.length > maxChars && !line2.endsWith("...")) {
      line2 = line2.slice(0, maxChars - 3).trimEnd() + "...";
    }
    return [line1, line2];
  } else if (line1) {
    if (line1.length > maxChars && !line1.endsWith("...")) {
      line1 = line1.slice(0, maxChars - 3).trimEnd() + "...";
    }
    return [line1];
  } else if (line2) {
    if (line2.length > maxChars && !line2.endsWith("...")) {
      line2 = line2.slice(0, maxChars - 3).trimEnd() + "...";
    }
    return [line2];
  } else {
    return [""];
  }
}
function calculateOptimalPixelsPerDay(phases, startDate, endDate, pageWidth, labelWidth) {
  const msPerDay = 864e5;
  const totalDays = Math.ceil(
    (endDate.getTime() - startDate.getTime()) / msPerDay
  );
  const chartWidth = pageWidth - labelWidth - 10;
  const basePixelsPerDay = Math.max(3, chartWidth / totalDays);
  if (phases.length <= 3) {
    return Math.min(15, basePixelsPerDay);
  }
  const phaseLengths = phases.map((phase) => {
    const days = Math.ceil(
      (phase.deadlineDate.getTime() - phase.startDate.getTime()) / msPerDay
    );
    return days;
  });
  const shortestSignificantPhase = Math.max(
    1,
    phaseLengths.filter((days) => days >= 1).sort((a, b) => a - b)[0] || 1
  );
  const minPixelsForShortestPhase = 15 / shortestSignificantPhase;
  const phaseStartDates = phases.map((p) => p.startDate.getTime());
  phaseStartDates.sort((a, b) => a - b);
  let densityFactor = 1;
  if (phaseStartDates.length > 1) {
    let totalGap = 0;
    for (let i = 1; i < phaseStartDates.length; i++) {
      totalGap += (phaseStartDates[i] - phaseStartDates[i - 1]) / msPerDay;
    }
    const avgGap = totalGap / (phaseStartDates.length - 1);
    if (avgGap < 7) {
      densityFactor = 1.5;
    } else if (avgGap < 14) {
      densityFactor = 1.2;
    }
  }
  const optimalPixelsPerDay = Math.max(
    minPixelsForShortestPhase,
    basePixelsPerDay * densityFactor
  );
  return Math.min(Math.max(3, optimalPixelsPerDay), 20);
}
function renderGanttChart(options) {
  const {
    phases,
    startDate: providedStartDate,
    endDate: providedEndDate,
    rowHeight = 30,
    barHeight = 20,
    pageWidth = 600,
    markIntervalDays,
    showTasks = false,
    fontFamily = "Arial",
    fontSize = 10
  } = options;
  let startDate = providedStartDate;
  let endDate = providedEndDate;
  if (!startDate || !endDate) {
    let minStartDate = null;
    let maxEndDate = null;
    phases.forEach((phase) => {
      if (!minStartDate || phase.startDate < minStartDate) {
        minStartDate = phase.startDate;
      }
      if (!maxEndDate || phase.deadlineDate > maxEndDate) {
        maxEndDate = phase.deadlineDate;
      }
      if (showTasks && phase.tasks) {
        phase.tasks.forEach((task) => {
          if (!minStartDate || task.startDate < minStartDate) {
            minStartDate = task.startDate;
          }
          if (!maxEndDate || task.deadlineDate > maxEndDate) {
            maxEndDate = task.deadlineDate;
          }
        });
      }
    });
    if (!startDate && minStartDate) {
      startDate = minStartDate;
    }
    if (!endDate && maxEndDate) {
      endDate = maxEndDate;
    }
  }
  if (!startDate) startDate = /* @__PURE__ */ new Date();
  if (!endDate) endDate = new Date(startDate.getTime() + 864e5);
  const msPerDay = 864e5;
  const totalDays = Math.ceil(
    (endDate.getTime() - startDate.getTime()) / msPerDay
  );
  const sorted = phases.slice().sort((a, b) => a.order - b.order);
  const topPad = 5;
  const cw = fontSize * 0.6;
  let maxChars = 0;
  sorted.forEach((ph) => {
    wrapAndTruncate(ph.title, 100).forEach(
      (l) => maxChars = Math.max(maxChars, l.length)
    );
    if (showTasks)
      ph.tasks?.forEach(
        (ts) => maxChars = Math.max(maxChars, ts.title.length)
      );
  });
  const labelWidth = Math.min(maxChars * cw + 20, pageWidth * 0.3);
  const labelPad = 10;
  const chartX = labelWidth + labelPad;
  const pixelsPerDay = options.pixelsPerDay || calculateOptimalPixelsPerDay(
    phases.filter((p) => p.startDate && p.deadlineDate),
    startDate,
    endDate,
    pageWidth,
    labelWidth
  );
  const interval = markIntervalDays ?? (pixelsPerDay < 10 ? 14 : 7);
  const daysPerSeg = Math.max(
    1,
    Math.floor((pageWidth - chartX) / pixelsPerDay)
  );
  const segCount = Math.ceil(totalDays / daysPerSeg);
  const results = [];
  for (let i = 0; i < segCount; i++) {
    const segStart = new Date(startDate.getTime() + i * daysPerSeg * msPerDay);
    const segDays = Math.min(daysPerSeg, totalDays - i * daysPerSeg);
    const segEnd = new Date(segStart.getTime() + segDays * msPerDay);
    const any = sorted.some(
      (ph) => ph.startDate < segEnd && ph.deadlineDate > segStart
    ) || showTasks && sorted.some(
      (ph) => ph.tasks?.some(
        (ts) => ts.startDate < segEnd && ts.deadlineDate > segStart
      )
    );
    if (!any) continue;
    const headerHt = fontSize * 1.4;
    const gridY = topPad + headerHt + 5;
    const rc = sorted.reduce(
      (c, ph) => c + 1 + (showTasks ? ph.tasks?.length ?? 0 : 0),
      0
    );
    const contentH = rc * rowHeight;
    const bottomPad = i < segCount - 1 ? 15 : 0;
    const svgH = topPad + headerHt + 5 + contentH + bottomPad;
    let gridSvg = "";
    for (let d = 0; d <= segDays; d += interval) {
      const x = d * pixelsPerDay;
      const dt = new Date(
        segStart.getTime() + d * msPerDay
      ).toLocaleDateString();
      gridSvg += `<line x1="${x}" y1="0" x2="${x}" y2="${contentH}" stroke="${COLORS.grid}"/>`;
      gridSvg += `<text x="${x + 2}" y="${-2}" font-size="${fontSize * 0.8}" fill="${COLORS.axisLabel}">${dt}</text>`;
    }
    let rows = "";
    let y = rowHeight / 2;
    sorted.forEach((ph) => {
      if (ph.startDate < segEnd && ph.deadlineDate > segStart) {
        const sx = Math.floor(
          (Math.max(ph.startDate.getTime(), segStart.getTime()) - segStart.getTime()) / msPerDay
        ) * pixelsPerDay;
        const ex = Math.floor(
          (Math.min(ph.deadlineDate.getTime(), segEnd.getTime()) - segStart.getTime()) / msPerDay
        ) * pixelsPerDay;
        const w = Math.max(1, ex - sx);
        const isClippedStart = ph.startDate < segStart;
        const isClippedEnd = ph.deadlineDate > segEnd;
        const cornerRadius = 3;
        if (isClippedStart || isClippedEnd) {
          const height = barHeight;
          const y1 = y - height / 2;
          const y2 = y + height / 2;
          let path = `M ${sx} ${y1}`;
          if (!isClippedEnd) {
            path += ` L ${ex - cornerRadius} ${y1}`;
            path += ` Q ${ex} ${y1} ${ex} ${y1 + cornerRadius}`;
          } else {
            path += ` L ${ex} ${y1}`;
          }
          if (!isClippedEnd) {
            path += ` L ${ex} ${y2 - cornerRadius}`;
            path += ` Q ${ex} ${y2} ${ex - cornerRadius} ${y2}`;
          } else {
            path += ` L ${ex} ${y2}`;
          }
          if (!isClippedStart) {
            path += ` L ${sx + cornerRadius} ${y2}`;
            path += ` Q ${sx} ${y2} ${sx} ${y2 - cornerRadius}`;
          } else {
            path += ` L ${sx} ${y2}`;
          }
          if (!isClippedStart) {
            path += ` L ${sx} ${y1 + cornerRadius}`;
            path += ` Q ${sx} ${y1} ${sx + cornerRadius} ${y1}`;
          } else {
            path += ` L ${sx} ${y1}`;
          }
          path += " Z";
          rows += `<path d="${path}" fill="${COLORS.phase}"/>`;
        } else {
          rows += `<rect x="${sx}" y="${y - barHeight / 2}" width="${w}" height="${barHeight}" fill="${COLORS.phase}" rx="${cornerRadius}" ry="${cornerRadius}"/>`;
        }
      }
      wrapAndTruncate(ph.title, Math.floor(labelWidth / cw)).forEach(
        (ln, idx, arr) => {
          const dy = (idx - (arr.length - 1) / 2) * fontSize * 1.2;
          rows += `<text x="${-labelPad}" y="${y + dy}" font-size="${fontSize}" fill="${COLORS.text}" text-anchor="end">${ln}</text>`;
        }
      );
      y += rowHeight;
      if (showTasks)
        ph.tasks?.forEach((ts) => {
          if (ts.startDate < segEnd && ts.deadlineDate > segStart) {
            const sx = Math.floor(
              (Math.max(ts.startDate.getTime(), segStart.getTime()) - segStart.getTime()) / msPerDay
            ) * pixelsPerDay;
            const ex = Math.floor(
              (Math.min(ts.deadlineDate.getTime(), segEnd.getTime()) - segStart.getTime()) / msPerDay
            ) * pixelsPerDay;
            const w = Math.max(1, ex - sx);
            const isClippedStart = ts.startDate < segStart;
            const isClippedEnd = ts.deadlineDate > segEnd;
            const cornerRadius = 3;
            if (isClippedStart || isClippedEnd) {
              const height = barHeight;
              const y1 = y - height / 2;
              const y2 = y + height / 2;
              let path = `M ${sx} ${y1}`;
              if (!isClippedEnd) {
                path += ` L ${ex - cornerRadius} ${y1}`;
                path += ` Q ${ex} ${y1} ${ex} ${y1 + cornerRadius}`;
              } else {
                path += ` L ${ex} ${y1}`;
              }
              if (!isClippedEnd) {
                path += ` L ${ex} ${y2 - cornerRadius}`;
                path += ` Q ${ex} ${y2} ${ex - cornerRadius} ${y2}`;
              } else {
                path += ` L ${ex} ${y2}`;
              }
              if (!isClippedStart) {
                path += ` L ${sx + cornerRadius} ${y2}`;
                path += ` Q ${sx} ${y2} ${sx} ${y2 - cornerRadius}`;
              } else {
                path += ` L ${sx} ${y2}`;
              }
              if (!isClippedStart) {
                path += ` L ${sx} ${y1 + cornerRadius}`;
                path += ` Q ${sx} ${y1} ${sx + cornerRadius} ${y1}`;
              } else {
                path += ` L ${sx} ${y1}`;
              }
              path += " Z";
              rows += `<path d="${path}" fill="${COLORS.task}"/>`;
            } else {
              rows += `<rect x="${sx}" y="${y - barHeight / 2}" width="${w}" height="${barHeight}" fill="${COLORS.task}" rx="${cornerRadius}" ry="${cornerRadius}"/>`;
            }
          }
          rows += `<text x="${-labelPad}" y="${y}" font-size="${fontSize * 0.8}" fill="${COLORS.text}" text-anchor="end">${ts.title}</text>`;
          y += rowHeight;
        });
    });
    const hdr = `${segStart.toLocaleDateString()} \u2192 ${new Date(
      segEnd.getTime() - 1
    ).toLocaleDateString()}`;
    let sep = "";
    if (i < segCount - 1) {
      const sy = gridY + contentH + bottomPad / 2;
      sep = `<line x1="0" y1="${sy}" x2="${pageWidth}" y2="${sy}" stroke="${COLORS.grid}" stroke-width="1"/>`;
    }
    const svg = `<svg width="${pageWidth}" height="${svgH}" xmlns="http://www.w3.org/2000/svg" font-family="${fontFamily}">
  <text x="5" y="${topPad + fontSize}" font-size="${fontSize * 1.2}" fill="${COLORS.text}">${hdr}</text>
  <g transform="translate(${chartX}, ${gridY})">
    ${gridSvg}
    ${rows}
  </g>
  ${sep}
</svg>`;
    results.push(svg);
  }
  return results;
}

// src/validation.ts
var validateLowestFee = ({
  lowestFee,
  lowestAnticipatedFee,
  highestAnticipatedFee,
  highestFee,
  fixedFee,
  cappedFee
}) => {
  if (lowestFee === null) {
    return "Lowest fee is required";
  } else if (lowestFee <= 0) {
    return "Fee should be more than \xA30";
  } else if (lowestAnticipatedFee !== null && lowestFee >= lowestAnticipatedFee) {
    return "Lowest fee should be less than lowest anticipated fee";
  } else if (highestAnticipatedFee !== null && lowestFee >= highestAnticipatedFee) {
    return "Lowest fee should be less than highest anticipated fee";
  } else if (highestFee !== null && lowestFee >= highestFee) {
    return "Lowest fee should be less than highest fee";
  } else if (fixedFee !== null && lowestFee > fixedFee) {
    return "Lowest fee should be less than fixed fee";
  } else if (cappedFee !== null && lowestFee > cappedFee) {
    return "Lowest fee should be less than capped fee";
  } else {
    return null;
  }
};
var validateHighestFee = ({
  lowestFee,
  lowestAnticipatedFee,
  highestAnticipatedFee,
  highestFee,
  fixedFee,
  cappedFee
}) => {
  if (highestFee === null) {
    return "Highest fee is required";
  } else if (highestFee <= 0) {
    return "Fee should be more than \xA30";
  } else if (lowestAnticipatedFee !== null && highestFee <= lowestAnticipatedFee) {
    return "Highest fee should be more than lowest anticipated fee";
  } else if (highestAnticipatedFee !== null && highestFee <= highestAnticipatedFee) {
    return "Highest fee should be more than highest anticipated fee";
  } else if (lowestFee !== null && highestFee <= lowestFee) {
    return "Highest fee should be more than lowest fee";
  } else if (fixedFee !== null && highestFee < fixedFee) {
    return "Highest fee should be more than fixed fee";
  } else if (cappedFee !== null && highestFee < cappedFee) {
    return "Highest fee should be more than capped fee";
  } else {
    return null;
  }
};
var validateAll = (props) => {
  if (validateLowestFee(props) !== null || validateHighestFee(props) !== null || props.fixedFee === null) {
    return false;
  } else {
    return true;
  }
};
export {
  renderFeeRiskProfileDiagram,
  renderGanttChart,
  renderRadialChart,
  validateAll,
  validateHighestFee,
  validateLowestFee
};
//# sourceMappingURL=index.mjs.map